import { useTranslation } from "react-i18next";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import { EditIcon } from "../../atoms/icons/EditIcon";
import { SecurityLockIcon } from "../../atoms/icons/SecurityLockIcon";
import { ViewOnIcon } from "../../atoms/icons/ViewIcons";
import { useState } from "react";
import { useUser } from "../../../graphql/hooks/rolesAndPermissions/useUser";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Modal from "../../molecules/Modal";
import ListTabBar from "../../molecules/ListTabBar";
import Subheading from "../../atoms/typography/Subheading";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Body from "../../atoms/typography/Body";
import BodySmall from "../../atoms/typography/BodySmall";
import ChipTag from "../../atoms/ChipTag";
import styled from "styled-components";
import { Col, Row } from "react-grid-system";
import TableRow from "../../atoms/TableRow";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import ManageUserModalContent from "./ManageUserModalContent";
import DeleteModal from "../../molecules/modals/DeleteModal";
import Alert from "../../molecules/Alert";
import { useDeleteSkynetUser } from "../../../graphql/hooks/rolesAndPermissions/useDeleteSkynetUser";
import GroupPermissionsCards from "../../molecules/users/GroupPermissionsCards";
import { PERMISSION_CATEGORIES } from "../../../models/permissionLabels";

interface Props {
  userId: string;
  isGroupAdmin?: boolean;
  onUserDeleted?: () => void;
  onUserUpdated?: () => void;
}

const GroupSectionRow = styled(Row)`
  background-color: ${theme.colors.grey02};
  padding: 4px 8px;
`;

const HandleUserActionRow: React.FC<Props> = ({
  userId,
  isGroupAdmin,
  onUserDeleted,
  onUserUpdated,
}) => {
  const { t } = useTranslation();
  const [accessRightsOpen, setAccessRightsOpen] = useState(false);
  const [selectedAccessTab, setSelectedAccessTab] = useState<"user" | "admin">(
    "user",
  );
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<
    string | undefined
  >(undefined);

  const {
    skynetUser,
    loading,
    error: userError,
  } = useUser(userId, !accessRightsOpen && !editUserOpen);

  const onCompletedDeleteUser = (result?: any) => {
    if (result?.rolesAndPermissions?.deleteSkynetUser?.success) {
      setDeleteErrorMessage(undefined);
      onUserDeleted?.();
    } else {
      setDeleteErrorMessage(t("errors.userCouldNotBeDeleted"));
    }
  };

  const onErrorDeleteUser = (e?: any) => {
    setDeleteErrorMessage(e?.message || t("errors.userCouldNotBeDeleted"));
  };

  const { deleteSkynetUser, loading: deleteLoading } = useDeleteSkynetUser({
    onCompleted: onCompletedDeleteUser,
    onError: onErrorDeleteUser,
  });

  const handleDeleteUser = async () => {
    try {
      await deleteSkynetUser({
        variables: {
          input: {
            skynetUserId: userId,
          },
        },
      });
    } catch (err) {
      console.error("Error deleting user:", err);
    }
  };

  const permissions = skynetUser?.effectivePermissions;

  const renderPermissionSection = ({
    key,
    titleKey,
    labels,
  }: (typeof PERMISSION_CATEGORIES)[number]) => {
    const perms: { read: string[]; write: string[] } | undefined =
      permissions?.[key];
    if (!perms) return null;
    const allPerms = [...new Set([...perms.read, ...perms.write])];
    if (allPerms.length === 0) return null;
    return (
      <div key={key}>
        <GroupSectionRow justify="between" align="center" nogutter>
          <Body fontweight="500">{t(titleKey)}</Body>
        </GroupSectionRow>
        {allPerms.map((perm) => (
          <TableRow key={perm}>
            <Col lg={4}>
              <BodySmall>{labels[perm] ? t(labels[perm]) : perm}</BodySmall>
            </Col>
            <Col
              lg={1.5}
              style={{ display: "flex", gap: 4, alignItems: "center" }}
            >
              {perms.read.includes(perm) ? (
                <CheckmarkIconSmall />
              ) : (
                <CrossIcon />
              )}
              <BodySmall>{t("users.read")}</BodySmall>
            </Col>
            <Col
              lg={1.5}
              style={{ display: "flex", gap: 4, alignItems: "center" }}
            >
              {perms.write.includes(perm) ? (
                <CheckmarkIconSmall />
              ) : (
                <CrossIcon />
              )}
              <BodySmall>{t("users.write")}</BodySmall>
            </Col>
          </TableRow>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {isGroupAdmin && (
          <IconButton
            icon={<SecurityLockIcon color={theme.colors.warning} />}
            onClick={() => {
              setSelectedAccessTab("admin");
              setAccessRightsOpen(!accessRightsOpen);
            }}
            size="medium"
            color={theme.colors.warning20}
          />
        )}
        <IconButton
          icon={<ViewOnIcon color={theme.colors.primary} />}
          onClick={() => {
            setSelectedAccessTab("user");
            setAccessRightsOpen(!accessRightsOpen);
          }}
          size="medium"
          color={theme.colors.primaryBg}
        />
        <IconButton
          icon={<EditIcon color={theme.colors.info} />}
          onClick={() => setEditUserOpen(true)}
          size="medium"
          color={theme.colors.info20}
        />
        <DeleteModal
          deleteButtonType="iconButtonMedium"
          onClickDelete={handleDeleteUser}
          message={t("users.deleteUserMessage")}
          disabled={deleteLoading}
        />
      </div>
      {deleteErrorMessage && (
        <Alert
          type="error"
          title={t("errors.userCouldNotBeDeleted")}
          message={deleteErrorMessage}
        />
      )}

      {accessRightsOpen && (
        <Modal closeIcon onClose={() => setAccessRightsOpen(false)} width={900}>
          <div>
            {isGroupAdmin && (
              <ListTabBar
                tabs={[
                  {
                    id: "admin",
                    tabLabelKey: "users.groupAdmin",
                  },
                  {
                    id: "user",
                    tabLabelKey: "users.myRights",
                  },
                ]}
                selectedId={selectedAccessTab}
                onSelectTabId={(id) =>
                  setSelectedAccessTab(id as "user" | "admin")
                }
              />
            )}
            <HorizontalSpacer spacing={1} />
            {userError && (
              <Alert
                type="error"
                title={t("errors.somethingWentWrong")}
                message={userError.message}
              />
            )}
            {selectedAccessTab === "admin" && (
              <div>
                {loading && <LoadingSpinner />}
                <Subheading>{t("users.groupAdmin")}</Subheading>
                <HorizontalSpacer spacing={0.5} />
                <BodySmall>
                  Som gruppadmin har du möjlighet att skapa upp nya grupper samt
                  tilldela användare specifika rättigheter. Nedan listas alla
                  rättigheter du har möjlighet att administrera.
                </BodySmall>
                <HorizontalSpacer spacing={0.5} />
                <BodySmall>
                  Du är gruppadministratör för:{" "}
                  <span
                    style={{
                      display: "inline-flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    {skynetUser?.groups
                      ?.filter((g) => g?.isGroupAdmin)
                      .map((g) => (
                        <ChipTag
                          key={g?.groupId}
                          tagTitleKey={`Gruppadmin - ${g?.groupName}`}
                        />
                      ))}
                  </span>
                </BodySmall>

                <HorizontalSpacer spacing={1} />
                <GroupPermissionsCards
                  groups={
                    skynetUser?.groups?.filter((g) => g?.isGroupAdmin) ?? []
                  }
                  variant="max"
                />
              </div>
            )}

            {selectedAccessTab === "user" && (
              <div>
                <Subheading>{t("users.myRights")}</Subheading>
                <HorizontalSpacer spacing={0.5} />
                <BodySmall>{skynetUser?.email}</BodySmall>
                <HorizontalSpacer spacing={0.5} />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {skynetUser?.groups?.map((group) => (
                    <ChipTag
                      key={group?.groupId}
                      tagTitleKey={
                        group?.isGroupAdmin
                          ? `${group?.groupName} - Gruppadmin`
                          : (group?.groupName ?? "")
                      }
                    />
                  ))}
                </div>
                <HorizontalSpacer spacing={2} />
                {loading && <LoadingSpinner />}
                <BodySmall fontweight="500">
                  {t("users.permissionsPerGroup")}
                </BodySmall>
                <HorizontalSpacer spacing={0.5} />
                <GroupPermissionsCards
                  groups={skynetUser?.groups ?? []}
                  variant="granted"
                />
                <HorizontalSpacer spacing={1.5} />
                <BodySmall fontweight="500">
                  {t("users.permissionsTotal")}
                </BodySmall>
                <HorizontalSpacer spacing={0.5} />
                {PERMISSION_CATEGORIES.map(renderPermissionSection)}
              </div>
            )}
          </div>
        </Modal>
      )}

      {editUserOpen && (
        <Modal closeIcon onClose={() => setEditUserOpen(false)} width={1000}>
          <div>
            <ManageUserModalContent
              user={skynetUser}
              onClose={() => setEditUserOpen(false)}
              onUserDeleted={onUserDeleted}
              onSuccess={onUserUpdated}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default HandleUserActionRow;
